import React, { useState, useEffect, useCallback } from 'react';
import { Sparkles, AlertCircle, CheckSquare, Square, Layers, RotateCcw } from 'lucide-react';
import { api } from '../../api/client';

const CAPABILITY_DEFAULT_PROMPT = `Identify every capability that was meaningfully discussed by each working group.

    A capability may include, but is not limited to:
    -Technologies,
    -Systems,
    -Platforms,
    -Infrastructure,
    -Operational concepts,
    -Organizational structures,
    -Policies,
    -Authorities,
    -Doctrine,
    -Training,
    -Human capital,
    -Partnerships,
    -Logistics and sustainment mechanisms,
    -Intelligence, command-and-control, communications, cyber, or decision-support functions.

    Do not limit the analysis to formally named capabilities. Convert descriptions of needed actions, functions, or effects into capability statements when appropriate.

    For each capability: 
    1. Provide a concise, normalized capability name. 
    2. Describe what the capability is intended to accomplish.`;

const ASSUMPTION_DEFAULT_PROMPT = `Identify assumptions made by each working group.
    Include both:
    -Explicit assumptions: Directly stated beliefs, premises, constraints, or expectations.
    -Implicit assumptions: Unstated beliefs that appear necessary for a participant's reasoning, recommendation, or conclusion to hold.

    Assumptions may concern:
    -Adversary behavior
    -Allied or partner behavior
    -Commercial-sector behavior
    -Political authorities
    -Governance and regulation
    -Public support
    -Economic conditions
    -Technology maturity
    -Availability of resources
    -Space access
    -Communications
    -Logistics
    -Data integrity
    -Decision speed
    -Human-machine trust
    
    For each assumption:
    1. State the assumption clearly as a complete proposition.
    2. Identify whether it was explicit or implicit.
    3. Cite or paraphrase the reasoning that reveals the assumption.`;

const DEFAULT_PROMPTS = { capability: CAPABILITY_DEFAULT_PROMPT, assumption: ASSUMPTION_DEFAULT_PROMPT };

const storageKey = (workshopId) => `cross-section-analysis:${workshopId}`;

// Persisted per workshop (browser-local, not shared across devices/teammates):
// which sections are selected (shared between the two analysis types, since
// you're usually asking different questions about the same section set), plus
// each type's own prompt and last result (so switching types never shows the
// wrong type's leftover results, and nothing is lost switching tabs or
// refreshing the page).
const loadPersisted = (workshopId) => {
  try {
    const raw = localStorage.getItem(storageKey(workshopId));
    if (!raw) throw new Error('none saved');
    const parsed = JSON.parse(raw);
    return {
      selectedIds: parsed.selectedIds || [],
      capability: { prompt: parsed.capability?.prompt || CAPABILITY_DEFAULT_PROMPT, result: parsed.capability?.result || null },
      assumption: { prompt: parsed.assumption?.prompt || ASSUMPTION_DEFAULT_PROMPT, result: parsed.assumption?.result || null },
    };
  } catch {
    return {
      selectedIds: [],
      capability: { prompt: CAPABILITY_DEFAULT_PROMPT, result: null },
      assumption: { prompt: ASSUMPTION_DEFAULT_PROMPT, result: null },
    };
  }
};

const CrossSectionAnalysisTab = ({ workshop }) => {
  const [sections, setSections] = useState([]);
  const [isLoadingSections, setIsLoadingSections] = useState(true);
  const [analysisType, setAnalysisType] = useState('capability');
  const [persisted, setPersisted] = useState(() => loadPersisted(workshop.id));
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState('');

  const loadSections = useCallback(async () => {
    setIsLoadingSections(true);
    try {
      const data = await api.getSectionsByWorkshop(workshop.id);
      setSections(data);
    } catch (err) {
      console.error('Failed to load sections:', err);
    } finally {
      setIsLoadingSections(false);
    }
  }, [workshop.id]);

  useEffect(() => { loadSections(); }, [loadSections]);

  // Persist on every change
  useEffect(() => {
    localStorage.setItem(storageKey(workshop.id), JSON.stringify(persisted));
  }, [persisted, workshop.id]);

  const selectedIds = persisted.selectedIds;
  const prompt = persisted[analysisType].prompt;
  const result = persisted[analysisType].result;

  const setPrompt = (newPrompt) => {
    setPersisted(prev => ({ ...prev, [analysisType]: { ...prev[analysisType], prompt: newPrompt } }));
  };

  const resetPromptToDefault = () => {
    setPersisted(prev => ({ ...prev, [analysisType]: { ...prev[analysisType], prompt: DEFAULT_PROMPTS[analysisType] } }));
  };

  const toggleSection = (sectionId, hasContent) => {
    if (!hasContent) return;
    setPersisted(prev => ({
      ...prev,
      selectedIds: prev.selectedIds.includes(sectionId)
        ? prev.selectedIds.filter(id => id !== sectionId)
        : [...prev.selectedIds, sectionId],
    }));
  };

  const handleRunAnalysis = async () => {
    if (selectedIds.length < 2 || !prompt.trim()) return;
    setIsRunning(true);
    setError('');
    try {
      const data = await api.crossSectionAnalysis(selectedIds, prompt);
      setPersisted(prev => ({ ...prev, [analysisType]: { ...prev[analysisType], result: data } }));
    } catch (err) {
      setError(err.message);
    } finally {
      setIsRunning(false);
    }
  };

  // Group sections by their writing session for a clearer picker
  const groupedSections = sections.reduce((acc, s) => {
    if (!acc[s.writing_session_id]) {
      acc[s.writing_session_id] = { name: s.writing_session_name, sections: [] };
    }
    acc[s.writing_session_id].sections.push(s);
    return acc;
  }, {});

  // Bucket results into all / some / only-one based on how many of the
  // sections in THAT run each item was found in.
  const buckets = (() => {
    if (!result) return null;
    const total = result.sections.length;
    const titleFor = (id) => result.sections.find(s => s.id === id)?.title || `Section ${id}`;
    const all = [];
    const some = [];
    const one = [];
    for (const item of result.items) {
      const withTitles = { ...item, titles: item.section_ids.map(titleFor) };
      if (item.section_ids.length >= total) all.push(withTitles);
      else if (item.section_ids.length === 1) one.push(withTitles);
      else some.push(withTitles);
    }
    return { all, some, one };
  })();

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-1">Cross-Section Analysis</h2>
        <p className="text-sm text-gray-500">
          Compare any two or more sections across every writing session in this workshop.
        </p>
      </div>

      {/* Analysis type toggle */}
      <div className="flex bg-white rounded-lg shadow-sm border border-gray-200 p-1 mb-6 w-fit">
        <button
          onClick={() => setAnalysisType('capability')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            analysisType === 'capability' ? 'bg-[#01006A] text-white' : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          Capability Comparison
        </button>
        <button
          onClick={() => setAnalysisType('assumption')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            analysisType === 'assumption' ? 'bg-[#01006A] text-white' : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          Assumption Comparison
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: section picker */}
        <div className="bg-white rounded-lg shadow-md p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
            <Layers className="w-4 h-4 text-[#01006A]" />
            Select Sections ({selectedIds.length} selected)
          </h3>
          <p className="text-xs text-gray-400 mb-3">Shared between both analysis types -- you're usually asking different questions about the same sections.</p>

          {isLoadingSections ? (
            <p className="text-sm text-gray-400">Loading sections…</p>
          ) : sections.length === 0 ? (
            <p className="text-sm text-gray-500">No sections in this workshop yet.</p>
          ) : (
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {Object.entries(groupedSections).map(([wsId, group]) => (
                <div key={wsId}>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-1">{group.name}</p>
                  <div className="space-y-1">
                    {group.sections.map(s => (
                      <button
                        key={s.id}
                        onClick={() => toggleSection(s.id, s.has_content)}
                        disabled={!s.has_content}
                        className={`w-full flex items-center gap-2 text-left px-2 py-1.5 rounded-lg text-sm transition-colors ${
                          !s.has_content ? 'text-gray-300 cursor-not-allowed' :
                          selectedIds.includes(s.id) ? 'bg-[#e6e6f5] text-[#000030]' : 'hover:bg-gray-50 text-gray-700'
                        }`}
                      >
                        {selectedIds.includes(s.id) ? <CheckSquare className="w-4 h-4 flex-shrink-0" /> : <Square className="w-4 h-4 flex-shrink-0" />}
                        <span className="truncate">{s.title}</span>
                        {!s.has_content && <span className="text-xs italic ml-auto flex-shrink-0">no synthesis yet</span>}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right: prompt + run */}
        <div className="bg-white rounded-lg shadow-md p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-gray-900">
              {analysisType === 'capability' ? 'Capability' : 'Assumption'} Analysis Prompt
            </h3>
            <button
              onClick={resetPromptToDefault}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-[#01006A]"
              title="Reset to the default prompt for this analysis type"
            >
              <RotateCcw className="w-3 h-3" />
              Reset to default
            </button>
          </div>
          <textarea
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            rows={10}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#01006A] resize-none"
          />
          <p className="text-xs text-gray-400 mt-1 mb-4">
            Your edits here are saved automatically, per analysis type, in this browser.
          </p>
          <button
            onClick={handleRunAnalysis}
            disabled={selectedIds.length < 2 || !prompt.trim() || isRunning}
            className="w-full flex items-center justify-center gap-2 bg-[#01006A] text-white py-3 rounded-lg hover:bg-[#010059] disabled:bg-gray-300"
          >
            <Sparkles className="w-4 h-4" />
            {isRunning ? 'Analyzing…' : selectedIds.length < 2 ? 'Select at least 2 sections' : `Compare ${selectedIds.length} Sections`}
          </button>
          {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
        </div>
      </div>

      {/* Results */}
      {buckets && (
        <div className="mt-8 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-gray-900">
              {analysisType === 'capability' ? 'Capability' : 'Assumption'} Comparison Results
            </h3>
            <span className="text-xs text-gray-400">from your last run of this analysis type</span>
          </div>
          <ResultGroup
            title={`Found in All ${result.sections.length} Sections`}
            items={buckets.all}
            colorClass="bg-[#e6e6f5] border-[#9696d4] text-[#000030]"
          />
          <ResultGroup
            title="Found in Some Sections"
            items={buckets.some}
            colorClass="bg-amber-50 border-amber-200 text-amber-900"
          />
          <ResultGroup
            title="Found in Only One Section"
            items={buckets.one}
            colorClass="bg-gray-50 border-gray-200 text-gray-700"
          />
          {buckets.all.length === 0 && buckets.some.length === 0 && buckets.one.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <AlertCircle className="w-8 h-8 mx-auto mb-2 text-gray-400" />
              <p className="text-sm">No items identified. Try adjusting the prompt.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ResultGroup = ({ title, items, colorClass }) => {
  if (items.length === 0) return null;
  return (
    <div>
      <h4 className="text-sm font-bold text-gray-900 mb-2">{title} ({items.length})</h4>
      <div className="space-y-2">
        {items.map((item, idx) => (
          <div key={idx} className={`border rounded-lg p-3 ${colorClass}`}>
            <p className="text-sm mb-2">{item.text}</p>
            <div className="flex flex-wrap gap-1.5">
              {item.titles.map((title, i) => (
                <span key={i} className="text-xs bg-white bg-opacity-60 px-2 py-0.5 rounded-full border border-current">
                  {title}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CrossSectionAnalysisTab;
